import React, { useState, useEffect, useCallback } from "react";
import FilterTabs from "../CustomFilterTabs";
import MainSearchBar from "../MainSearchbar";
import CustomCards from "../CustomCards";
import Drawer from "../Drawer";
import { useParams } from "react-router-dom";
import {
  useGetAllDocumentsList,
  useGetDocumentDetails,
  useGetProviderDocuments,
} from "../../custom-hooks/queries";
import CustomCardsSkeleton from "../CustomCardsSkeleton";
import Searchbar from "../Search";
import Notification from "../Notification";
import { useDispatch, useSelector } from "react-redux";
import { setDrawer } from "../../redux-store/features/layoutSlice";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useScreenWidth } from "../../custom-hooks/useScreenWidth";
import Pagination from "../CustomPagination";
import FIltersDropdown from "../Filters/FiltersDropdown";
import { setSearchTextByPage } from "../../redux-store/features/providerFiltersSlice";
import { debounce } from "lodash";
import { getLocationFromProvider } from "../../utils/addressUtils";

const Documents = ({
  data,
  Services,
  error,
  isError,
  isLoading,
  cardsRefetch,
  setSelectedIds,
  selectedIds,
  searchQuery,
}) => {
  const [selectedFilterTab, setSelectedFilterTab] = useState("View All");
  const [filterData, setFilterData] = useState([]);

  const { id } = useParams();
  // const { data, isLoading, isError, error } = useGetProviderDocuments(id);
  const [show, setShow] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [noDocs, setNoDocs] = useState(false);

  // State to manage drawer visibility and selected document
  // const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const dispatch = useDispatch();
  const isDrawerOpen = useSelector((state) => state.layout.drawerOpen);
  const [docId, setDocId] = useState("");

  const {
    data: drawerData,
    isPending: drawerLoading,
    isSuccess: drawerIsSuccess,
    refetch,
  } = useGetDocumentDetails(docId);

  useEffect(() => {
    if (isError) {
      setShow(true);
    } else {
      setShow(false);
    }
  }, [isError]);

  useEffect(() => {
    // Filter the data based on the selected tab and search text
    if (data?.items) {
      let filteredData = data.items;

      // Apply tab filtering
      if (selectedFilterTab !== "View All") {
        filteredData = filteredData.filter(
          (item) => item.document_type.toLowerCase() === selectedFilterTab
        );
      }

      // Apply search filtering
      if (searchQuery) {
        filteredData = filteredData.filter((item) =>
          item.document_name.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      setFilterData(filteredData);
    }
  }, [data, selectedFilterTab, searchQuery]);

  const handleFilterTabChange = (tab) => {
    setSelectedFilterTab(tab);
  };

  // Function to open the drawer with the selected document's details
  const openDrawer = (documentId) => {
    // mutate(documentId);
    dispatch(setDrawer(true));
  };

  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;
  const { rfis } = useSelector((state) => state.rfisColors);

  const filters = [
    {
      id: "docType",
      name: "Document Type",
      options: [
        { docId: "RFI", label: "VQQ", checked: false },
        { docId: "VQA", label: "Audit Report", checked: false },
      ],
      total: 2,
    },

    {
      id: "status",
      name: "Status",
      options: [
        { statusID: "Released", label: "Released", checked: false },
        { statusID: "Pending", label: "Pending", checked: false },
        { statusID: "Published", label: "Published", checked: false },
        { statusID: "Rejected", label: "Rejected", checked: false },
        { statusID: "Upcoming", label: "Upcoming", checked: false },
      ],
      total: 5,
    },
    {
      id: "services",
      name: "Services",
      options: rfis,
      total: 33,
    },
  ];

  const debouncedOnSubmit = useCallback(
    debounce((search) => {
      dispatch(
        setSearchTextByPage({
          pageKey: "myDocuments",
          searchText: searchQuery,
        })
      );
    }, 300),
    [dispatch]
  );

  const onSubmit = (search) => {
    debouncedOnSubmit(search);
  };

  const getDocLocation = (item, short = true) => {
    return getLocationFromProvider({
      address: item?.document_address || item?.document_location,
      city: item?.document_city,
      state: item?.document_state,
      country: item?.document_country
    }, { short, abbvState: true });
  };

  return (
    <div className="p-6">
      <div className="items-center">
        <div className="flex justify-between items-center">
          <MainSearchBar
            pageKey="myDocuments"
            placeholder="Search documents..."
            height="40px"
            width={
              screenWidth <= 375
                ? "17.5rem"
                : screenWidth < 425
                ? "22rem"
                : "25rem"
            }
            marginRight={"mr-2"}
            isSearchIcon={true}
            onSearch={onSubmit}
          />

          <FIltersDropdown
            rfis={rfis}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
            filters={filters}
          />
        </div>
      </div>

      {/* Card container */}
      {isLoading ? (
        <CustomCardsSkeleton isProviderDetail={true} />
      ) : data?.items?.length === 0 ? (
        <div
          className="flex flex-col items-center  justify-center text-center space-y-2 "
          style={{
            marginTop: "100px",
          }}
        >
          <span className="p-3 rounded-full bg-custom-link-bg-color">
            <MagnifyingGlassIcon
              width={20}
              height={20}
              style={{ color: "white" }}
            />
          </span>
          <p className="text-lg text-gray-700 font-semibold">
            {"No documents found"}
          </p>
          <p className="text-gray-500">
            {"Your search did not match any documents. Please try again."}
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap justify-center lg:justify-start gap-4 mt-5">
          {isLoading ? (
            <CustomCardsSkeleton isProviderDetail={true} />
          ) : (
            data?.items.map((item, index) => {
              return (
                <div
                  className={`flex justify-center w-full sm:w-auto ${
                    data?.items.length % 3 !== 0 &&
                    index === data?.items.length - 1
                      ? "sm:ml-0"
                      : ""
                  }`}
                  key={index}
                >
                  <CustomCards
                    documentType={item?.entity_type}
                    name={item.provider_name}
                    status={item?.document_status}
                    services={item.capabilities}
                    publishedDate={item.published_on}
                    requestedDate={item?.requested_on}
                    onClick={() => {
                      setDocId(item?.document_id);
                      dispatch(setDrawer(true));
                    }}
                    documentName={item?.document_name}
                    entityId={item?.entity_id}
                    docLocation={getDocLocation(item)}
                    docLocationFull={getDocLocation(item, false)}
                    docId={item?.document_id}
                    details={item}
                    questionnaireId={item?.hc_questionnaire_id || null}
                  />
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Drawer Component */}
      {isDrawerOpen && (
        <Drawer
          open={isDrawerOpen}
          // onClose={closeDrawer} // Close the drawer
          docId={drawerData?.document_id}
          docType={drawerData?.document_type}
          docName={drawerData?.document_name}
          documents={drawerData}
          Services={Services}
          refetch={refetch}
          libraryRefetch={cardsRefetch}
          drawerLoading={drawerLoading}
        />
      )}

      <Notification
        show={show}
        setShow={setShow}
        errorMessage={error?.message}
        status={"error"}
      />
      {!isLoading && filterData.length > 21 && (
        <div className="mt-10">
          <Pagination
            totalPages={data?.total_pages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default Documents;
